// Renders a streaming session's captions: the finalized-segment list plus a trailing
// tentative row for the current partial (WEBSTREAM-3). Reuses TranscriptPlayer's
// segment/segment-speaker/segment-text styling (see styles.css) so a streamed transcript
// looks like the same component family as a batch one — only `.caption-partial` is new, for
// the in-progress row.
//
// NEVER log segment text — this component only ever renders it into the DOM.
import { useEffect, useRef } from 'react';
import type { StreamingSegment } from '@scribe/core/streaming';
import type { StreamingStatus } from '../hooks/useStreamingSession';
import { formatSpeaker, speakerColor } from './ui';

export interface LiveCaptionsProps {
  finalized: StreamingSegment[];
  partial: StreamingSegment | null;
  status: StreamingStatus;
  /** WAVE1-17 (web-ui-issues #17): true once the in-session toggle has swapped back to
   * Record-only but this session already accumulated live captions — dims the rows and
   * swaps the heading note so it reads as "these are being superseded", rather than either
   * looking like a fresh/authoritative live feed or vanishing outright. Caller-driven: this
   * component has no opinion on *when* that's true, only how to render it. */
  provisional?: boolean;
}

function emptyStateLabel(status: StreamingStatus): string | null {
  switch (status) {
    case 'connecting':
      return 'Connecting…';
    case 'live':
      return 'Listening…';
    case 'stopping':
      return 'Finishing up…';
    default:
      return null;
  }
}

/** How close to the bottom (px) still counts as "following the live feed". Generous enough to
 * survive sub-pixel rounding and a partial row growing under the user. Not load-bearing — tune
 * freely. */
const STICK_THRESHOLD_PX = 48;

export function LiveCaptions({ finalized, partial, status, provisional = false }: LiveCaptionsProps) {
  const listRef = useRef<HTMLDivElement | null>(null);
  // Starts true: a fresh caption list is at the bottom by definition, so auto-follow is on
  // until the user scrolls away from it.
  const stickToBottomRef = useRef(true);

  // Auto-follow scrolls the captions LIST, not the element into view: `scrollIntoView` scrolls
  // every scrollable ancestor including the document, so during a live session (a new partial
  // roughly once a second) it repeatedly yanked the whole page back down and undid the user's
  // own scrolling before they finished reading (M10 dev validation 2026-08-07).
  // Instant, never smooth — and that is a correctness decision, not a taste one. A smooth
  // scroll animates over several frames while new partials keep appending, so the animation
  // lands short of the grown scrollHeight; the final scroll event then measures a gap past
  // STICK_THRESHOLD_PX, `handleScroll` sets stickToBottom false, and auto-follow latches OFF
  // with no user scroll left to switch it back on. Distinguishing our own animation frames
  // from the user's would need a programmatic-scroll flag; a feed that updates about once a
  // second does not need the animation in the first place.
  useEffect(() => {
    const el = listRef.current;
    if (!el || !stickToBottomRef.current) return;
    el.scrollTo({ top: el.scrollHeight, behavior: 'auto' });
  }, [finalized, partial]);

  // Scrolling up to re-read an earlier caption switches auto-follow off; scrolling back to the
  // bottom switches it on again.
  function handleScroll() {
    const el = listRef.current;
    if (!el) return;
    stickToBottomRef.current =
      el.scrollHeight - el.scrollTop - el.clientHeight <= STICK_THRESHOLD_PX;
  }

  const isEmpty = finalized.length === 0 && partial === null;
  const emptyLabel = isEmpty ? emptyStateLabel(status) : null;

  return (
    <div className={'live-captions' + (provisional ? ' live-captions-provisional' : '')}>
      <h2 className="subhead">Live transcript</h2>
      {provisional && (
        <p className="muted live-captions-provisional-note">
          Live captions — final transcript will replace these
        </p>
      )}
      <div className="live-captions-list" ref={listRef} onScroll={handleScroll}>
        {emptyLabel && <p className="muted">{emptyLabel}</p>}
        {finalized.map((seg, i) => (
          <div key={i} className="segment caption-row">
            <div className="segment-meta">
              <span
                className="segment-speaker"
                style={{ color: speakerColor(seg.speaker_label) }}
              >
                {formatSpeaker(seg.speaker_label)}
              </span>
            </div>
            <p className="segment-text">{seg.text}</p>
          </div>
        ))}
        {partial && (
          <div className="segment caption-row caption-partial">
            <div className="segment-meta">
              <span
                className="segment-speaker"
                style={{ color: speakerColor(partial.speaker_label) }}
              >
                {formatSpeaker(partial.speaker_label)}
              </span>
            </div>
            <p className="segment-text">{partial.text}</p>
          </div>
        )}
      </div>
    </div>
  );
}
